import {
  collection,
  addDoc,
  getDocs,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  Flow,
  Collaborators,
  Feedback,
  Logs,
  Unlockedfeatures,
  User,
  CustomProperty,
  Template,
  //TemplateSubscribers
} from "../types/types";
import { db } from "../firebase";
import { TreeCheckboxSelectionKeys } from "primereact/tree";

function cleanUndefined(obj) {
  if (Array.isArray(obj)) {
    return obj.map(cleanUndefined);
  } else if (obj && typeof obj === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = cleanUndefined(value);
      }
      // If you want to replace undefined with null instead, use:
      // cleaned[key] = value === undefined ? null : cleanUndefined(value);
    }
    return cleaned;
  }
  return obj;
}

// Function to load flow data from Firestore
export const loadFlowData = async (flowId: string) => {
  try {
    const docRef = doc(db, "flows", flowId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (e) {
    console.error("Error loading flow data: ", e);
    return null;
  }
};

// Function to save flow data to Firestore
export const saveFlowData = async (flowId: string, data: any) => {
  try {
    const existingFlow = await loadFlowData(flowId);
    if (existingFlow && existingFlow.email !== data.email) {
      data.email = existingFlow.email; // Prevent overwriting the owner's email
    }
    data.favourite = existingFlow
      ? existingFlow.favourite
        ? existingFlow.favourite
        : false
      : false;

    // Check for undefined values in data
    const undefinedKeys = Object.keys(data).filter(
      (key) => data[key] === undefined,
    );
    if (undefinedKeys.length > 0) {
      console.error(
        `Error: The following keys have undefined values and cannot be saved to Firestore: ${undefinedKeys.join(
          ", ",
        )}`,
      );
      return {
        success: false,
        error: `Undefined values for keys: ${undefinedKeys.join(", ")}`,
      };
    }

    const cleanedData = cleanUndefined(data);
    if (cleanedData !== data) {
      const diffKeys = Object.keys(data).filter(
        (key) => JSON.stringify(data[key]) !== JSON.stringify(cleanedData[key]),
      );
      // Validate both keys and values
      const originalKeys = Object.keys(data).sort();
      const cleanedKeys = Object.keys(cleanedData).sort();
      const keysIdentical =
        JSON.stringify(originalKeys) === JSON.stringify(cleanedKeys);
      const valuesIdentical = diffKeys.length === 0;

      if (!keysIdentical) {
        // console.warn(
        //   "Key mismatch between original and cleaned data:",
        //   { originalKeys, cleanedKeys }
        // );
      }

      if (!valuesIdentical) {
        // console.warn(
        //   "Differences between original and cleaned data values:",
        //   diffKeys.reduce((acc, key) => {
        // acc[key] = { original: data[key], cleaned: cleanedData[key] };
        // return acc;
        //   }, {} as Record<string, any>)
        // );
      }

      if (keysIdentical && valuesIdentical) {
        // console.log(
        //   "Both keys and values are identical between original and cleaned data.",
        // );
      }
    }
    data = cleanedData;

    await setDoc(doc(db, "flows", flowId), data);
    return { success: true, error: null };
  } catch (error) {
    // console.log(data);
    navigator.clipboard.writeText(JSON.stringify(data));
    console.error("Error saving flow data: " + error);
    return { success: false, error: error };
  }
};

export const loadFlowProps = async (flowId: string) => {
  try {
    const docRef = doc(db, "flowProps", flowId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return Array.isArray(data) ? data : Object.values(data);
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (e) {
    console.error("Error loading flow props: ", e);
    return null;
  }
};

// Function to save flow props to Firestore
export const saveFlowProps = async (flowId: string, data: any) => {
  try {
    await setDoc(doc(db, "flowProps", flowId), { ...data });
    return { success: true, error: null };
  } catch (error) {
    console.log(data);
    console.error("Error saving flow props: " + error);
    return { success: false, error: error };
  }
};

// Function to add a collaborator to Firestore
export const addCollaborator = async (
  flowId: string,
  owner: string,
  email: string,
  role: "editor" | "viewer" = "editor",
) => {
  try {
    const docRef = doc(db, "collaborators", flowId);
    const docSnap = await getDoc(docRef);

    const collaborator = { email, role };

    if (docSnap.exists()) {
      const data = docSnap.data();
      const collaborators = data.collaborators || [];
      collaborators.push(collaborator);
      await setDoc(docRef, {
        ...data,
        collaborators,
        modifiedAt: new Date().toISOString(),
      });
    } else {
      await setDoc(docRef, {
        owner,
        collaborators: [collaborator],
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error("Error adding collaborator: ", e);
  }
};

// Function to remove a collaborator from Firestore
export const removeCollaborator = async (flowId: string, email: string) => {
  try {
    const docRef = doc(db, "collaborators", flowId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const collaborators = data.collaborators.filter(
        (collaborator: string | { email: string; role: string }) => {
          // Handle both string format (old) and object format (new)
          if (typeof collaborator === "string") {
            return collaborator !== email;
          }
          return collaborator.email !== email;
        },
      );
      await setDoc(docRef, {
        ...data,
        collaborators,
        modifiedAt: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error("Error removing collaborator: ", e);
  }
};

// Function to load collaborators from Firestore
export const loadCollaborators = async (flowId: string) => {
  try {
    const docRef = doc(db, "collaborators", flowId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const collaborators = data.collaborators || [];

      // Handle migration: if collaborators are strings, convert to objects
      return collaborators.map((c: any) => {
        if (typeof c === "string") {
          return { email: c, role: "editor" };
        }
        return c;
      });
    } else {
      return [];
    }
  } catch (e) {
    console.error("Error loading collaborators: ", e);
    return [];
  }
};

// Get current user's role for a flow
export const getUserRole = async (
  flowId: string,
  userEmail: string,
): Promise<"owner" | "editor" | "viewer" | null> => {
  try {
    const docRef = doc(db, "collaborators", flowId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();

      // Check if owner
      if (data.owner === userEmail) {
        return "owner";
      }

      // Check if collaborator
      const collaborators = data.collaborators || [];
      const collaborator = collaborators.find((c: any) => {
        // Handle both old format (string) and new format (object)
        const email = typeof c === "string" ? c : c.email;
        return email === userEmail;
      });

      if (collaborator) {
        // If old format (string), default to editor
        return typeof collaborator === "string"
          ? "editor"
          : collaborator.role || "editor";
      }

      return null;
    }

    return null;
  } catch (e) {
    console.error("Error getting user role: ", e);
    return null;
  }
};

// Function to fetch flows from Firestore
export const fetchFlows = async (email: string) => {
  try {
    // Fetch flows owned by the user
    const q = query(collection(db, "flows"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    const ownedFlows = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      name: doc.data().name,
      email: doc.data().email,
      favourite: doc.data().favourite,
      timestamp: doc.data().timestamp,
      role: "owner",
      ...doc.data(),
    }));

    // Fetch all collaborator records and filter for this user
    const collaboratorsSnapshot = await getDocs(
      collection(db, "collaborators"),
    );
    const collaboratorFlows: any[] = [];

    for (const collaboratorDoc of collaboratorsSnapshot.docs) {
      const data = collaboratorDoc.data();
      const collaborators = data.collaborators || [];

      // Check if user is in the collaborators array
      const userCollaborator = collaborators.find((c: any) => {
        const collaboratorEmail = typeof c === "string" ? c : c.email;
        return collaboratorEmail === email;
      });

      if (userCollaborator) {
        // Fetch the flow document
        const flowDoc = await getDoc(doc(db, "flows", collaboratorDoc.id));
        if (flowDoc.exists()) {
          const userRole =
            typeof userCollaborator === "string"
              ? "editor"
              : userCollaborator.role || "editor";
          collaboratorFlows.push({
            id: flowDoc.id,
            name: flowDoc.data().name,
            email: flowDoc.data().email,
            favourite: flowDoc.data().favourite,
            timestamp: flowDoc.data().timestamp,
            role: userRole,
            ...flowDoc.data(),
          });
        }
      }
    }

    // Combine owned flows and collaborator flows
    return [...ownedFlows, ...collaboratorFlows];
  } catch (e) {
    console.error("Error fetching flows: ", e);
    return [];
  }
};

export const getUserPlan = async (user: any) => {
  try {
    if (user) {
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        return userData.plan.toLowerCase();
      }
    }
    return null;
  } catch (e) {
    console.error("Error getting user plan: ", e);
    return null;
  }
};

export const addFeedback = async (email: string, feedback: string) => {
  try {
    // Save to Firebase first
    await addDoc(collection(db, "feedback"), {
      email,
      feedback,
      timestamp: new Date().toISOString(),
    });

    // Send email notification via Mailgun
    // Import dynamically to avoid blocking if email fails
    const { sendFeedbackEmail } = await import("../services/emailService");
    await sendFeedbackEmail(email, feedback);
  } catch (e) {
    console.error("Error adding feedback: ", e);
    // Even if email fails, feedback is still saved in Firebase
  }
};

export const unlockedFeature = async (featureId: string): Promise<boolean> => {
  const featureDoc = await getDoc(doc(db, "unlockedfeatures", featureId));
  if (featureDoc.exists()) {
    const { startDate, endDate } = featureDoc.data();
    const now = new Date();
    const canBeFeatured =
      now >= new Date(startDate) && now <= new Date(endDate);
    return canBeFeatured;
  }
  return false;
};

export const getUnlockedFeatures = async (): Promise<string[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, "unlockedfeatures"));
    const now = new Date();
    const unlockedFeatures = querySnapshot.docs
      .filter((doc) => {
        const { startDate, endDate } = doc.data();
        return (
          now >= new Date(startDate.toDate()) &&
          now <= new Date(endDate.toDate())
        );
      })
      .map((doc) => doc.id);
    // alert(unlockedFeatures);
    return unlockedFeatures;
  } catch (e) {
    console.error("Error getting unlocked features: ", e);
    return [];
  }
};

export const updateUserShowWelcome = async (
  userId: string,
  showWelcome: boolean,
) => {
  try {
    await setDoc(doc(db, "users", userId), { showWelcome }, { merge: true });
  } catch (e) {
    console.error("Error updating showWelcome: ", e);
  }
};

export const updateUserInsights = async (
  userId: string,
  insights: TreeCheckboxSelectionKeys,
) => {
  try {
    await updateDoc(doc(db, "users", userId), { insights });
  } catch (e) {
    console.error("Error updating user insights: ", e);
  }
};

export const updateUserSettings = async (
  userId: string,
  settings: {
    presentWithShowHideButtons: boolean;
    currency: string;
    showWhyTag?: boolean;
    showStartupDialog?: boolean;
    showAIPromptOnNewMap?: boolean;
    aiModel?: string;
    autoSave?: boolean;
    theme?: string;
    defaultNodeType?: string;
    edgeType?: string;
  },
) => {
  try {
    await updateDoc(doc(db, "users", userId), { settings });
  } catch (e) {
    console.error("Error updating user settings: ", e);
  }
};

// Function to get user document from Firestore
export const getUserDoc = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    console.error("Error getting user document: ", error);
    return null;
  }
};

// Function to check user's plan and flow limit
export const checkUserPlanAndFlowLimit = async (
  userId: string,
  email: string,
) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.plan === "free") {
        const flowsQuery = query(
          collection(db, "flows"),
          where("email", "==", email),
        );
        const flowsSnapshot = await getDocs(flowsQuery);
        if (flowsSnapshot.size >= 2) {
          return { limitReached: true };
        }
      }
    }
    return { limitReached: false };
  } catch (error) {
    console.error("Error checking user plan and flow limit:", error);
    return { limitReached: false, error };
  }
};

// Function to get user data by email from Firestore
export const getUserDataByEmail = async (email: string) => {
  try {
    const q = query(collection(db, "users"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data();
    }
    return null;
  } catch (error) {
    console.error("Error getting user data by email:", error);
    return null;
  }
};

export const updateOrCreateUserDoc = async (
  user: any,
  email?: string,
): Promise<boolean> => {
  try {
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      await updateDoc(userDocRef, {
        modifiedAt: new Date(),
      });
      return userDoc.data().useLocalData || false;
    } else {
      await setDoc(userDocRef, {
        email: email || user.email,
        name: user.displayName || "",
        plan: "free",
        useLocalData: false,
        showWelcome: true,
        createdAt: new Date(),
        modifiedAt: new Date(),
        insights: {
          keys: [],
        } as TreeCheckboxSelectionKeys,
      });
      return false;
    }
  } catch (error) {
    console.error("Error updating or creating user document:", error);
    return false;
  }
};

export const updateUseLocalData = async (
  userId: any,
  useLocalData: boolean,
): Promise<boolean> => {
  try {
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      await updateDoc(userDocRef, {
        useLocalData: useLocalData,
      });
    }
    return true;
  } catch (error) {
    console.error("Error updating useLocalData:", error);
    return false;
  }
};

export const deleteFlowData = async (flowId: string) => {
  try {
    await deleteDoc(doc(db, "flows", flowId));
    // Also delete the local cached image
    const { deleteFlowImage } = await import("./dexie-db");
    await deleteFlowImage(flowId).catch(() => {
      // Ignore if image doesn't exist
    });
  } catch (error) {
    console.error("Error deleting flow data: ", error);
  }
};

export const loadTemplate = async (id: string) => {
  try {
    // New canonical collection name: user_templates
    const primaryRef = doc(db, "user_templates", id);
    const primarySnap = await getDoc(primaryRef);
    if (primarySnap.exists()) return primarySnap.data();

    // Backward compatibility: older installs used `templates`
    const legacyRef = doc(db, "templates", id);
    const legacySnap = await getDoc(legacyRef);
    if (legacySnap.exists()) return legacySnap.data();

    console.log("No such document!");
    return null;
  } catch (e) {
    console.error("Error loading template: ", e);
    return null;
  }
};

export const saveTemplate = async (id: string, data: any) => {
  try {
    const existingTemplate = await loadTemplate(id);
    if (existingTemplate && existingTemplate.email !== data.email) {
      data.email = existingTemplate.email;
    }
    await setDoc(doc(db, "user_templates", id), data);
    return { success: true, error: null };
  } catch (error) {
    console.error("Error saving template: " + error);
    return { success: false, error: error };
  }
};

export const deleteTemplate = async (id: string) => {
  try {
    // Prefer deleting from the canonical collection. Also attempt legacy cleanup.
    await deleteDoc(doc(db, "user_templates", id));
    try {
      await deleteDoc(doc(db, "templates", id));
    } catch {
      // ignore legacy deletion failures
    }
  } catch (error) {
    console.error("Error deleting template: ", error);
  }
};

export const loadMyTemplates = async (email: string): Promise<Template[]> => {
  try {
    // Canonical collection
    const primaryQuery = query(
      collection(db, "user_templates"),
      where("email", "==", email),
    );
    const primarySnap = await getDocs(primaryQuery);

    const byId = new Map<string, Template>();
    primarySnap.forEach((d) => {
      const t = d.data() as Template;
      if (t?.id) byId.set(t.id, t);
    });

    // Backward compatibility: legacy `templates`
    const legacyQuery = query(
      collection(db, "templates"),
      where("email", "==", email),
    );
    const legacySnap = await getDocs(legacyQuery);
    legacySnap.forEach((d) => {
      const t = d.data() as Template;
      if (t?.id && !byId.has(t.id)) byId.set(t.id, t);
    });

    return Array.from(byId.values());
  } catch (e) {
    console.error("Error loading template: ", e);
    return [];
  }
};

// export const addTemplateSubscriber = async (
//   flowId: string,
//   owner: string,
//   email: string
// ) => {
//   try {
//     const docRef = doc(db, "template_subscribers", flowId);
//     const docSnap = await getDoc(docRef);

//     if (docSnap.exists()) {
//       const data = docSnap.data();
//       const subscribers = data.subscribers || [];
//       subscribers.push(email);
//       await setDoc(docRef, {
//         ...data,
//         collaborators,
//         modifiedAt: new Date().toISOString(),
//       });
//     } else {
//       await setDoc(docRef, {
//         owner,
//         collaborators: [email],
//         createdAt: new Date().toISOString(),
//         modifiedAt: new Date().toISOString(),
//       });
//     }
//   } catch (e) {
//     console.error("Error adding collaborator: ", e);
//   }
// };

// // Function to remove a collaborator from Firestore
// export const removeCollaborator = async (flowId: string, email: string) => {
//   try {
//     const docRef = doc(db, "collaborators", flowId);
//     const docSnap = await getDoc(docRef);

//     if (docSnap.exists()) {
//       const data = docSnap.data();
//       const collaborators = data.collaborators.filter(
//         (collaborator: string) => collaborator !== email
//       );
//       await setDoc(docRef, {
//         ...data,
//         collaborators,
//         modifiedAt: new Date().toISOString(),
//       });
//     }
//   } catch (e) {
//     console.error("Error removing collaborator: ", e);
//   }
// };

// // Function to load collaborators from Firestore
// export const loadCollaborators = async (flowId: string) => {
//   try {
//     const docRef = doc(db, "collaborators", flowId);
//     const docSnap = await getDoc(docRef);

//     if (docSnap.exists()) {
//       return docSnap.data().collaborators || [];
//     } else {
//       return [];
//     }
//   } catch (e) {
//     console.error("Error loading collaborators: ", e);
//     return [];
//   }
// };

export const getBusinessModuleSubscriber = async (
  email: string,
  id: string,
) => {
  try {
    if (email && id) {
      const docRef = doc(db, "business_module_subscribers", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const subscribers = userData.subscribers || [];
        return subscribers.includes(email);
      }
    }
    return false;
  } catch (e) {
    console.error("Error getting user plan: ", e);
    return false;
  }
};

// Function to save flow to history with time-based throttling
// Only saves if the last history entry is older than the throttle window (default 15 minutes)
export const saveFlowToHistory = async (
  flowId: string,
  flowData: any,
  throttleMinutes: number = 15,
  title?: string,
) => {
  try {
    // Check the most recent history entry for this flow
    // Query without orderBy to avoid composite index requirement
    const recentHistoryQuery = query(
      collection(db, "flows_history"),
      where("flowId", "==", flowId),
    );

    const recentSnapshot = await getDocs(recentHistoryQuery);

    // Check if we should throttle the save
    if (!recentSnapshot.empty) {
      // Find most recent timestamp from all results
      let mostRecentTimestamp = 0;
      recentSnapshot.forEach((doc) => {
        const data = doc.data();
        const timestamp = new Date(data.timestamp).getTime();
        if (timestamp > mostRecentTimestamp) {
          mostRecentTimestamp = timestamp;
        }
      });

      const now = Date.now();
      const minutesSinceLastSave = (now - mostRecentTimestamp) / (1000 * 60);
      // Skip saving if within throttle window
      if (minutesSinceLastSave < throttleMinutes) {
        // console.log(
        //   `⏱️ History save throttled: Last save was ${minutesSinceLastSave.toFixed(1)} minutes ago (threshold: ${throttleMinutes} minutes)`,
        // );
        return { success: true, error: null, throttled: true };
      }
    }

    // Save to history if no recent entry or outside throttle window
    const historyId = `${flowId}_${Date.now()}`;
    await setDoc(doc(db, "flows_history", historyId), {
      flowId: flowId,
      flowData: flowData,
      timestamp: new Date().toISOString(),
      title: title || null,
    });
    // console.log(`💾 Flow history saved for ${flowId}`);
    return { success: true, error: null, throttled: false };
  } catch (error) {
    console.error("Error saving flow to history: ", error);
    return { success: false, error: error, throttled: false };
  }
};

// Function to load flow history versions for a specific flow
export const loadFlowHistory = async (
  flowId: string,
  limitCount: number = 200,
) => {
  try {
    // Query by root-level flowId without orderBy to avoid composite index requirement
    const historyQuery = query(
      collection(db, "flows_history"),
      where("flowId", "==", flowId),
    );

    const snapshot = await getDocs(historyQuery);

    const histories: any[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      histories.push({
        id: doc.id,
        flowId: data.flowId,
        flowData: data.flowData,
        timestamp: data.timestamp,
        createdAt: data.createdAt,
        title: data.title,
      });
    });

    // Sort by timestamp descending in memory
    histories.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    });

    // Apply limit after sorting
    return histories.slice(0, limitCount);
  } catch (e) {
    console.error("Error loading flow history: ", e);
    return [];
  }
};

// Function to restore a flow from history
export const restoreFlowFromHistory = async (
  flowId: string,
  historyId: string,
) => {
  try {
    const historyDoc = await getDoc(doc(db, "flows_history", historyId));
    if (historyDoc.exists()) {
      const data = historyDoc.data();
      // Handle both old structure (previousData) and new structure (flowData)
      const restoredFlowData = data.previousData || data.flowData;

      if (!restoredFlowData) {
        return {
          success: false,
          error: "No flow data found in history document",
        };
      }

      // Save the restored flow data
      await saveFlowData(flowId, restoredFlowData);
      return { success: true, flowData: restoredFlowData, error: null };
    } else {
      return {
        success: false,
        error: "History document not found",
      };
    }
  } catch (error) {
    console.error("Error restoring flow from history: ", error);
    return { success: false, error: error };
  }
};
